import { Map as ImmutableMap } from 'immutable';
import { createSelector } from 'reselect';

import { categoriesSelectors } from '../categories';

import type RootState from 'app-components/utils/redux-store/rootState';
import type { CATEGORIES_REDUCER_KEY, Category } from '../categories/categoriesTypes';
import { FILTERS_REDUCER_KEY, type Filter, mkFilterObject } from './filtersTypes';
import { convertQCSFilterToFilterObject } from './filtersUtils';

type State = Pick<RootState, typeof FILTERS_REDUCER_KEY>;

export const getFilterStore = (state: State) => state[FILTERS_REDUCER_KEY];
export const getFilters = (state: State) => getFilterStore(state).resourcesById;
export const getLoadPending = (state: State) => getFilterStore(state).loadPending;
export const getIsLoading = (state: State) => getFilterStore(state).isLoading;
export const getError = (state: State) => getFilterStore(state).error;
export const getLastSyncDate = (state: State) => getFilterStore(state).lastSyncDate;

export const getFilter = createSelector(
  getFilters,
  (_state: unknown, filterId: string) => filterId,
  (filters, filterId) => {
    return filters?.get(filterId) || null;
  },
);

export const getFilterObjectFromFilter = createSelector(getFilter, (filter) => {
  if (!filter) {
    return { filter: mkFilterObject(), type: 'CHARTOFACCOUNT' };
  }
  return {
    filter: convertQCSFilterToFilterObject(filter),
    // note - type is important for watchlists and planned items that can only have one filter item (payees, tags, categories, etc) in them
    type: filter?.filterItems?.[0].type || '',
  };
});

export const getCategoryNameFromCOAFilter = createSelector(
  categoriesSelectors.getCategoriesById as (
    state: Pick<RootState, CATEGORIES_REDUCER_KEY>,
  ) => ImmutableMap<string, Category>,
  getFilters,
  (categories, filters) =>
    ImmutableMap().withMutations((mutable) => {
      filters.forEach((filter: Filter) => {
        filter.filterItems?.forEach((filterItem) => {
          if (
            filterItem.type === 'CHARTOFACCOUNT' &&
            filterItem?.chartOfAccounts &&
            categories.has(filterItem?.chartOfAccounts?.[0]?.id)
          ) {
            mutable.set(filter.id, categories?.get(filterItem?.chartOfAccounts?.[0]?.id));
          }
        });
      });
    }),
);
